import { useState } from "react"
import { motion, AnimatePresence } from "motion/react"
import { useNavigate } from "react-router"
import { ArrowLeft } from "@untitledui/icons"
import { useOrganization } from "@/lib/organization/organization-context"
import { EstimateProvider } from "@/lib/estimates/estimate-context"
import { cx } from "@/lib/utils/cx"
import { useQuickEstimateState } from "../quick-estimate/use-quick-estimate-state"
import { StepErrorBoundary } from "../quick-estimate/error-boundary"
import { ConfirmLeave } from "../wizard-shared/confirm-leave"
import { InputSteps, type InputStepIndex } from "../wizard-shared/input-steps"
import { NoTeamPicked } from "../wizard-shared/no-team-picked"
import { ProgressRail } from "../wizard-shared/progress-rail"
import { useEstimateCapture } from "../wizard-shared/use-estimate-capture"
import { useReturnUrl } from "../wizard-shared/use-return-url"
import { StepSend } from "../quick-estimate/step-send"
import { StepProcessing } from "./step-processing"
import { StepDamage } from "./step-damage"
import { StepOperations } from "./step-operations"
import { StepReview } from "./step-review"
import {
  FE_WIZARD_COPY,
  FE_CUSTOMER_EYEBROW,
  FE_CUSTOMER_CONTINUE_CTA,
} from "./copy"

// Full Estimate wizard.
//
// 8 logical steps. Steps 0–2 reuse the shared input phase
// (Vehicle/Photos/Tags) — same components and pipeline as QE3, with
// FE-specific eyebrow labels. Step 3 reuses QE3's StepSend in
// "continue" mode to collect customer info before the agent run.
// Steps 4–7 are FE-only:
//   • Step 4 — Processing: fires the create + damage agent sequence
//     (no /major-operations; damage already returns line items with
//     action + partType). Renders inside the input card chrome.
//   • Step 5 — Damage editor: edits batched, on Next: PUT /line-items
//     + POST /sections-orchestrator → ping.
//   • Step 6 — Operations (read-only): displays operations the
//     orchestrator generated as children of line items.
//   • Step 7 — Review: status-only save, BE-PDF download, total avg.

const STEP_LABELS = [
  "Vehicle",
  "Photos",
  "Tags",
  "Customer",
  "Generate",
  "Damage",
  "Operations",
  "Review",
] as const

type DisplayStep = 0 | 1 | 2 | 3 | 4 | 5 | 6 | 7

const INPUT_EYEBROWS = {
  vehicle: "Step 1 of 8 · Vehicle",
  photos: "Step 2 of 8 · Photos",
  tags: "Step 3 of 8 · Tags",
}

export function FullEstimateWizard() {
  const { activeTeamId } = useOrganization()
  const teamId = activeTeamId

  if (!teamId) {
    return (
      <NoTeamPicked
        emoji="🏬"
        title={FE_WIZARD_COPY.noTeamTitle}
        body={FE_WIZARD_COPY.noTeamBody}
        ctaLabel={FE_WIZARD_COPY.noTeamCta}
      />
    )
  }

  return (
    <EstimateProvider initialData={{ teamId }}>
      <FullEstimateInner />
    </EstimateProvider>
  )
}

function FullEstimateInner() {
  const navigate = useNavigate()
  const returnUrl = useReturnUrl()
  const { state, actions, hydrated } = useQuickEstimateState()
  const { capture, cameraNodes } = useEstimateCapture(actions)

  // Post-input steps. When null we mirror the persisted QE3
  // state.step (0–2 covers vehicle/photos/tags). Steps 3–5 are
  // not persisted because they require a server estimateId — if
  // the user refreshes, we restart from the input flow.
  const [postStep, setPostStep] = useState<
    "customer" | "process" | "damage" | "operations" | "review" | null
  >(null)
  const [createdEstimateId, setCreatedEstimateId] = useState<number | null>(
    null,
  )

  // Mirror QE3's persisted step until the user moves into post-input
  // (process/edit/review). state.step is constrained to 0-3; treat
  // input step 3 as Tags since FE post-input is tracked separately.
  const inputStep: InputStepIndex = hydrated
    ? (Math.min(state.step, 2) as InputStepIndex)
    : 0

  const displayStep: DisplayStep =
    postStep === "review"
      ? 7
      : postStep === "operations"
        ? 6
        : postStep === "damage"
          ? 5
          : postStep === "process"
            ? 4
            : postStep === "customer"
              ? 3
              : inputStep

  const [confirmingLeave, setConfirmingLeave] = useState(false)

  const goToInputStep = (n: 0 | 1 | 2) => {
    setPostStep(null)
    actions.setStep(n)
  }

  // Customer (3) and Generate (4) both ride the centered card chrome
  // alongside the input steps. Damage / Operations / Review (5–7)
  // render full-width because they ship their own cards.
  const stepIsInsideCard = displayStep <= 4

  return (
    <div className="min-h-[calc(100vh-3.5rem)] bg-gradient-to-b from-zinc-50 to-white">
      {/* Background dot grid (matches QE3). */}
      <div aria-hidden className="pointer-events-none fixed inset-0">
        <div
          className="absolute inset-0 opacity-[0.35]"
          style={{
            backgroundImage:
              "radial-gradient(circle at 1px 1px, rgb(228 228 231 / 0.55) 1px, transparent 0)",
            backgroundSize: "24px 24px",
          }}
        />
      </div>

      <div
        className={cx(
          "relative mx-auto px-4 md:px-8 py-6 md:py-10",
          stepIsInsideCard ? "max-w-[640px]" : "max-w-3xl",
        )}
      >
        {/* Top exit + step counter */}
        <div className="flex items-center justify-between mb-5">
          <button
            type="button"
            onClick={() => setConfirmingLeave(true)}
            className="inline-flex items-center gap-1.5 h-9 -ml-1 pl-1.5 pr-3 rounded-lg text-[13px] font-medium text-zinc-500 hover:text-zinc-900 hover:bg-zinc-100/70 transition-all duration-150"
          >
            <ArrowLeft className="size-3.5" aria-hidden />
            {FE_WIZARD_COPY.leaveLabel}
          </button>
          <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-zinc-400">
            {STEP_LABELS[displayStep]}
          </span>
        </div>

        {stepIsInsideCard ? (
          <div className="rounded-3xl border border-zinc-200/70 bg-white shadow-2xl shadow-zinc-900/[0.07] overflow-hidden">
            <ProgressRail step={displayStep} labels={STEP_LABELS} />
            <div className="p-5 md:p-10">
              <AnimatePresence mode="wait" initial={false}>
                <motion.div
                  key={displayStep}
                  initial={{ opacity: 0, y: 18, scale: 0.985 }}
                  animate={{ opacity: 1, y: 0, scale: 1 }}
                  exit={{ opacity: 0, y: -18, scale: 0.985 }}
                  transition={{
                    type: "spring",
                    stiffness: 260,
                    damping: 30,
                    mass: 0.9,
                    opacity: { duration: 0.25 },
                  }}
                >
                  {displayStep <= 2 && (
                    <InputSteps
                      step={displayStep as InputStepIndex}
                      state={state}
                      actions={actions}
                      capture={capture}
                      onAdvancePastTags={() => setPostStep("customer")}
                      eyebrows={INPUT_EYEBROWS}
                    />
                  )}
                  {displayStep === 3 && (
                    <StepErrorBoundary stepLabel="Customer">
                      <StepSend
                        vin={state.vin}
                        vehicle={state.vehicle}
                        photos={state.photos}
                        notes={state.notes}
                        customer={state.customer}
                        actions={actions}
                        capture={capture}
                        mode="continue"
                        onContinue={() => setPostStep("process")}
                        onBack={() => goToInputStep(2)}
                        eyebrowLabel={FE_CUSTOMER_EYEBROW}
                        continueCtaLabel={FE_CUSTOMER_CONTINUE_CTA}
                      />
                    </StepErrorBoundary>
                  )}
                  {displayStep === 4 && (
                    <StepErrorBoundary stepLabel="Generate">
                      <StepProcessing
                        inputs={{
                          vin: state.vin,
                          vehicle: state.vehicle,
                          photos: state.photos,
                          notes: state.notes,
                          customer: state.customer,
                        }}
                        onBack={() => setPostStep("customer")}
                        onReady={(estimateId) => {
                          setCreatedEstimateId(estimateId)
                          setPostStep("damage")
                        }}
                      />
                    </StepErrorBoundary>
                  )}
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        ) : (
          // Steps 5–7 (damage / operations / review): full-width, no
          // outer card. The step components ship their own section
          // cards internally.
          <div className="space-y-5">
            <ProgressRail step={displayStep} labels={STEP_LABELS} />
            <AnimatePresence mode="wait" initial={false}>
              <motion.div
                key={displayStep}
                initial={{ opacity: 0, y: 18 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -18 }}
                transition={{
                  type: "spring",
                  stiffness: 260,
                  damping: 30,
                  mass: 0.9,
                }}
              >
                {displayStep === 5 && createdEstimateId !== null && (
                  <StepErrorBoundary stepLabel="Damage">
                    <StepDamage
                      estimateId={createdEstimateId}
                      onNext={() => setPostStep("operations")}
                      onBack={() => setPostStep("process")}
                    />
                  </StepErrorBoundary>
                )}
                {displayStep === 6 && createdEstimateId !== null && (
                  <StepErrorBoundary stepLabel="Operations">
                    <StepOperations
                      estimateId={createdEstimateId}
                      onNext={() => setPostStep("review")}
                      onBack={() => setPostStep("damage")}
                    />
                  </StepErrorBoundary>
                )}
                {displayStep === 7 && createdEstimateId !== null && (
                  <StepErrorBoundary stepLabel="Review">
                    <StepReview
                      estimateId={createdEstimateId}
                      onBack={() => setPostStep("operations")}
                    />
                  </StepErrorBoundary>
                )}
              </motion.div>
            </AnimatePresence>
          </div>
        )}
      </div>

      {cameraNodes}

      <ConfirmLeave
        open={confirmingLeave}
        title={FE_WIZARD_COPY.leaveConfirmTitle}
        body={FE_WIZARD_COPY.leaveConfirmBody}
        continueLabel={FE_WIZARD_COPY.leaveConfirmContinue}
        exitLabel={FE_WIZARD_COPY.leaveConfirmExit}
        onCancel={() => setConfirmingLeave(false)}
        onConfirm={() => navigate(returnUrl ?? "/dashboard")}
      />
    </div>
  )
}
